/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import ContextProcessor from '../context/context-processor';
import Constants from '../constants';
import type { ConfigurationType } from '../initialiser/configurations';
import type { ActionObject } from '../globals';
import type { RemoteCaseTypeMetadata } from '../environment-info/types';
import EnvironmentInfo from '../environment-info';

const REMOTE_SYSTEM_ID = '.RemoteSystemID';
const REMOTE_APPLICATION_TYPE_TRADITIONAL = 'Traditional';
export const REMOTE_CASE_VIEW = 'pyRemoteCaseDetails';

class RemoteCase {
  static remoteCaseMapping: NonNullable<ConfigurationType['remoteCaseMapping']> = {};

  /**
   * * [setRemoteCaseMapping]
   * Description : Sets the remote case mapping object.
   * @param mapping captures the case remote relationship object
   * eq.,
   * {
   *   Work-Host:{
   *     RemoteClassName: "Work-Remote",
   *     RemoteSystemID: "SystemName",
   *     RemoteApplicationType: "Traditional"
   *   }
   * }
   */
  static setRemoteCaseMapping = (mapping: NonNullable<ConfigurationType['remoteCaseMapping']>) => {
    this.remoteCaseMapping = mapping;
    this.setAdditionalParameters();
  };

  /**
   * * [setRemoteCaseMapping]
   * Description : returns the captured remote case mapping.
   * @returns returns the captured mapping
   * eq.,
   * {
   *   Work-Host:{
   *     RemoteClassName: "Work-Remote",
   *     RemoteSystemID: "SystemName",
   *     RemoteApplicationType: "Traditional"
   *   }
   * }
   */
  static getRemoteCaseMapping = () => this.remoteCaseMapping;

  /**
   * * [getRemoteCase]
   * Description : Sets the remote case mapping object.
   * @param hostCaseClass host case class
   * @returns the mapped object
   * eq.,
   * {
   *     RemoteClassName: "Work-Remote",
   *     RemoteSystemID: "SystemName",
   *     RemoteApplicationType: "Traditional"
   *   }
   */
  static getRemoteCase = (hostCaseClass: string) => this.remoteCaseMapping?.[hostCaseClass];

  /**
   * * [getRemoteCase]
   * Description : Sets the remote case mapping object.
   * @param hostCaseClass host case class
   * @returns Returns the mapped object
   * eq.,
   * {
   *     RemoteClassName: "Work-Remote",
   *     RemoteSystemID: "SystemName"
   *   }
   */
  static isRemoteCase = (hostCaseClass?: string) =>
    !!(hostCaseClass && this.remoteCaseMapping && this.remoteCaseMapping[hostCaseClass]);

  /**
   * * [getRemoteHeaderByContext]
   * Description : Sets the remote case mapping object.
   * @param context context name
   * @returns Returns mapped header
   */
  static getRemoteHeaderByContext = (context: string): string => {
    return ContextProcessor.getProperty(REMOTE_SYSTEM_ID, Constants.CASE_INFO.HEADERS, context);
  };

  /**
   * * [getRemoteHeader]
   * Description : Sets the remote case mapping object.
   * @param caseClass hostclass name
   * @returns Returns mapped header
   */
  static getRemoteHeader = (caseClass: string) => {
    return this.remoteCaseMapping[caseClass]?.RemoteSystemID;
  };

  static injectRemoteCaseHeader = (
    headers: NonNullable<ActionObject['headers']>,
    payload: { caseTypeID: string } | undefined,
    context: string
  ) => {
    if (headers && headers.RemoteSystemID) return;

    const RemoteSystemID = this.getRemoteHeaderByContext(context);
    if (RemoteSystemID) {
      headers.RemoteSystemID = RemoteSystemID;
    } else if (payload && this.isRemoteCase(payload.caseTypeID)) {
      headers.RemoteSystemID = this.getRemoteHeader(payload.caseTypeID);
    }
  };

  static isTraditionalRemoteCase = (caseClassName: string = ''): boolean => {
    return RemoteCase.getRemoteCase(caseClassName)?.RemoteApplicationType === REMOTE_APPLICATION_TYPE_TRADITIONAL;
  };

  static setAdditionalParameters = (): void => {
    const remoteCaseTypeList: RemoteCaseTypeMetadata[] | undefined =
      EnvironmentInfo.environmentInfoObject?.pyRemoteCaseTypeList;
    remoteCaseTypeList?.forEach((caseTypeMetadata: RemoteCaseTypeMetadata) => {
      const remoteCase = this.getRemoteCase(caseTypeMetadata.pyWorkTypeImplementationClassName);
      if (remoteCase) {
        remoteCase.name = caseTypeMetadata.pyWorkTypeName;
      }
    });
  };

  static getRemoteHeaderFromSourceClass = (remoteClassName: string): string | undefined => {
    const remoteCaseDetails = Object.values(this.remoteCaseMapping).find(
      (details) => details.RemoteClassName === remoteClassName
    );
    return remoteCaseDetails?.RemoteSystemID;
  };

  static getRemoteCaseClassBySourceClass = (remoteClassName: string): string | undefined => {
    for (const [key, value] of Object.entries(this.remoteCaseMapping)) {
      if (value.RemoteClassName === remoteClassName) {
        return key;
      }
    }
  };
}

export default RemoteCase;
